import {
  Camera,
  CameraListResponse,
  CameraStatusResponse,
  CameraSnapshotResponse,
  CreateCameraRequest,
  UpdateCameraRequest,
  CameraStatus,
  CameraSnapshot,
} from "../types";
import api from "./api.ts";

/**
 * Camera Service
 * Handles all camera-related API operations for IQWatch devices
 */
export const cameraService = {
  /**
   * 获取设备的所有 Camera
   * GET /entrio/api/iqwatch-devices/:id/cameras
   */
  async getCameras(deviceId: string): Promise<CameraListResponse> {
    const response = await api.get(`/iqwatch-devices/${deviceId}/cameras`);
    return response.data;
  },

  /**
   * 创建 Camera 绑定
   * POST /entrio/api/iqwatch-devices/:id/cameras
   */
  async createCamera(
    deviceId: string,
    data: CreateCameraRequest
  ): Promise<Camera> {
    const response = await api.post(
      `/iqwatch-devices/${deviceId}/cameras`,
      data
    );
    return response.data;
  },

  /**
   * 更新 Camera
   * PUT /entrio/api/iqwatch-devices/:id/cameras/:cameraId
   */
  async updateCamera(
    deviceId: string,
    cameraId: string,
    data: UpdateCameraRequest
  ): Promise<Camera> {
    const response = await api.put(
      `/iqwatch-devices/${deviceId}/cameras/${cameraId}`,
      data
    );
    return response.data;
  },

  /**
   * 删除 Camera
   * DELETE /entrio/api/iqwatch-devices/:id/cameras/:cameraId
   */
  async deleteCamera(deviceId: string, cameraId: string): Promise<void> {
    await api.delete(`/iqwatch-devices/${deviceId}/cameras/${cameraId}`);
  },

  /**
   * 获取 Camera 状态（从缓存）
   * GET /entrio/api/iqwatch-devices/:id/cameras/:cameraId/status
   */
  async getCameraStatus(
    deviceId: string,
    cameraId: string
  ): Promise<CameraStatusResponse> {
    const response = await api.get(
      `/iqwatch-devices/${deviceId}/cameras/${cameraId}/status`
    );
    return response.data;
  },

  /**
   * 获取 Camera 快照
   * GET /entrio/api/iqwatch-devices/:id/cameras/:cameraId/snapshot
   */
  async getCameraSnapshot(
    deviceId: string,
    cameraId: string
  ): Promise<CameraSnapshotResponse> {
    const response = await api.get(
      `/iqwatch-devices/${deviceId}/cameras/${cameraId}/snapshot`
    );
    const data = response.data;

    // Transform API response to match expected structure
    // API returns: { device, camera: { id, cloud_id, name }, snapshot: { status, snapshot_url, last_update } }
    // Expected: { camera_id, snapshot: { url, timestamp, cloud_id, name } }
    if (data && data.snapshot) {
      // Check if snapshot has snapshot_url (new format) or url (old format)
      const snapshotUrl = data.snapshot.snapshot_url || data.snapshot.url;
      const timestamp =
        data.snapshot.last_update || data.snapshot.timestamp || "";

      // Get camera info - could be in data.camera or needs to be extracted
      const cameraIdFromResponse = data.camera?.id || cameraId;
      const cloudId = data.camera?.cloud_id || data.snapshot.cloud_id || "";
      const cameraName = data.camera?.name || data.snapshot.name || null;

      if (snapshotUrl) {
        const transformed = {
          camera_id: cameraIdFromResponse,
          snapshot: {
            url: snapshotUrl,
            timestamp: timestamp,
            cloud_id: cloudId,
            name: cameraName,
          },
        };
        return transformed;
      }
    }

    // If data already has the expected structure, return as-is
    if (data && data.snapshot && data.snapshot.url) {
      return data;
    }

    // Fallback - try to construct minimal response
    const fallback = {
      camera_id: cameraId,
      snapshot: {
        url: data?.snapshot?.snapshot_url || data?.snapshot?.url || "",
        timestamp:
          data?.snapshot?.last_update || data?.snapshot?.timestamp || "",
        cloud_id: data?.camera?.cloud_id || "",
        name: data?.camera?.name || null,
      },
    };
    return fallback;
  },

  /**
   * 直接 API 访问 - 获取状态（绕过缓存）
   * GET /entrio/api/iqwatch-devices/cameras/:cloud_id/status
   */
  async getCameraStatusDirect(cloudId: string): Promise<{
    cloud_id: string;
    camera_status: CameraStatus;
  }> {
    const response = await api.get(
      `/iqwatch-devices/cameras/${cloudId}/status`
    );
    return response.data;
  },

  /**
   * 直接 API 访问 - 获取快照（绕过缓存）
   * GET /entrio/api/iqwatch-devices/cameras/:cloud_id/snapshot
   */
  async getCameraSnapshotDirect(cloudId: string): Promise<{
    cloud_id: string;
    snapshot: CameraSnapshot;
  }> {
    const response = await api.get(
      `/iqwatch-devices/cameras/${cloudId}/snapshot`
    );
    return response.data;
  },
};

/**
 * Error handling helper for camera operations
 */
export async function handleCameraOperation<T>(
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.error ||
        error.response.data?.message ||
        "Unknown error";

      switch (status) {
        case 400:
          // Handle business logic errors
          if (errorMessage.includes("Maximum 4 cameras")) {
            throw new Error("Each device can only bind up to 4 cameras");
          } else if (errorMessage.includes("already exists")) {
            throw new Error("The camera has already been bound to this device");
          } else if (errorMessage.includes("does not belong")) {
            throw new Error("The camera does not belong to this device");
          } else if (errorMessage.includes("cloud_id is required")) {
            throw new Error("The cloud_id is required");
          } else {
            throw new Error(errorMessage);
          }
        case 404:
          if (errorMessage.includes("not yet synced")) {
            throw new Error(
              "The camera data has not yet been synced, please try again later"
            );
          } else if (errorMessage.includes("Device not found")) {
            throw new Error("The device was not found");
          } else if (errorMessage.includes("Camera not found")) {
            throw new Error("The camera was not found");
          } else {
            throw new Error("The camera or device was not found");
          }
        case 500:
          throw new Error("Server error, please try again later");
        default:
          throw new Error(errorMessage || "Unknown error");
      }
    } else {
      throw new Error("Network error, please check your network connection");
    }
  }
}
